export default {
  title: 'Workspace Settings',
  tab: {
    account: 'Profile',
    settings: 'Settings',
    general: 'General',
    members: 'Users',
    theme: 'Theme',
    pricing: 'Pricing',
    billing: 'Billing',
    notifications: 'Notifications',
    integrations: 'Integrations',
    youtube: 'YouTube',
    intercom: 'Intercom',
    salesforce: 'Salesforce',
    security: 'Security',
    stripe: 'Stripe',
    elevenlabs: 'Elevenlabs',
    email: 'Email Digest',
    accredible: 'Accredible',
    webhook: 'Webhook',
    mcp: 'Trainn MCP'
  },
  workspace: {
    heading: 'Workspace Settings',
    display_name: 'Workspace Name',
    description: 'This is the name of your workspace.',
    placeholder: 'Yoda',
    error: 'Please enter a product space name',
    min_length_error: 'Workspace name should have a minimum of 4 characters',
    domain_name: 'Domain Name',
    domain_description: 'This is the domain name of your workspace.',
    language: 'Default Language',
    language_description:
      'This is the default language for which you can add voice overs.',
    language_contact:
      'Your current plan supports adding voice overs in only one language. You cannot change it later.',
    language_contact_1:
      'Your current plan supports adding voice overs in only one language.',
    language_contact_2: ' to change this language or ',
    language_contact_3: ' to use multiple languages',
    api_key_label: 'API Key',
    api_key_description:
      'Use the below key to access our APIs. Make sure that this key is stored at your server side in a secure manner.',
    api_key_regenerate_description:
      'Once you regenerate your API key, your old key will be accessible for 24 hours. All integrations using your old key needs to be updated. Contact us at support@trainn.co if you wish to disable your old key immediately.',
    regenerate_label: 'Regenerate API Key',
    mau: {
      1: `You've used up `,
      2: ' MAUs'
    },
    switch: 'Switch Workspace'
  },
  account: {
    heading: 'Account Settings',
    label: 'Name',
    change_password: 'Update your workspace password?',
    update_password: 'Update Password',
    bio: 'Bio',
    password: {
      old_label: 'Old Password',
      new_label: 'New Password',
      success: 'Your password has been changed successfully.'
    },
    designation: 'Designation',
    change_photo: 'Change Photo',
    profile_image_desc: 'Upto 1200 x 1200',
    email_notifications: 'Receive engagement notifications via email'
  },
  theme: {
    workspace: 'Workspace',
    guide: 'Guide',
    video: 'Video',
    glossary: 'Glossary',
    glossary_settings: {
      title: 'Translation Glossary (%{count})',
      desc: 'Add your key terms to ensure consistent translations. You can add up to 5,000 terms. Contact support to increase your limit.',
      list_description: 'List of translated terms (%{count})',
      search_placeholder: 'Search terms ....',
      delete_selected: 'Delete (%{count})',
      sync_button: 'Sync',
      sync_tooltip_synced: 'All changes are synced',
      sync_action: 'Action required to sync the terms',
      publish_pending: 'Publish pending',
      publish_up_to_date: 'Publish Up to Date',
      source_term: 'Original Term',
      source_language: 'Original Language',
      target_language: 'Translation Language',
      target_term: 'Translated Term',
      empty_search: 'No terms matched your search "%{search}".',
      empty_filter: 'No terms matched for the filtered language.',
      empty_state: 'No glossary terms found. Click "Add" to create your first term.',
      confirm_bulk_delete: 'Are you sure you want to delete %{count} glossary term?',
      confirm_bulk_delete_plural: 'Are you sure you want to delete %{count} glossary terms?',
      add_term: 'Add Term',
      bulk_import: 'Bulk Import',
      not_enabled: 'Glossary is not enabled for your workspace.',
      contact_support: 'Contact support',
      add_modal: {
        title: 'Add Term',
        description: 'Add a term to translate consistently across all your content.',
        source_language: 'Original Language',
        source_text: 'Original Term',
        target_language: 'Translation Language',
        target_text: 'Translated Term',
        add_button: 'Add'
      }
    },
    logo: {
      label: 'Your Logo',
      description:
        'Upload your logo to appear on all the pages that you share.'
    },
    color: {
      label: 'Brand Color',
      description: 'Choose your brand color.',
      hex: 'Or Hex'
    },
    bannerTextColor: {
      label: 'Banner Text Color',
      description:
        'Text color in portal banner will be customized to the color selected.'
    },
    font: {
      label: 'Brand Font',
      description: `Enter your brand font and select where you'd like it to appear.`,
      upgrade_desc: 'This feature is not available with your current plan.',
      place_holder: 'Google Font Name',
      note: 'You can only enter Google Fonts. If you want to use a different font, please',
      edit_note: 'If you want to change your brand font, please',
      remove_button: 'Remove',
      remove_success: 'Your brand font removed successfully.',
      academy: 'Academy',
      hub: 'Knowledge Hub',
      intro_clip: 'Video Intros',
    }
  },
  video_theme: {
    upgrade_note: 'This section allows you to customize the appearance of your videos. These features are not available in your current plan.',
    upgrade_button: 'Upgrade to Start Customizing',
    save_button: 'Save Changes',
    intro_outro: {
      title: 'Intro and Outro Settings',
      desc: 'Upload an intro and outro clip that will be used for all your videos.',
      intro: 'Intro Clip',
      outro: 'Outro Clip',
      button: 'Upload Image/Video',
      note: 'If you do not upload an intro clip, Trainn will automatically generate a default intro slide. Choose the settings for this slide below.',
      download_error: 'Download failed',
      limit_note: 'Image: Max 5 MB (1280×720 px) · Video: Max 25 MB (640×480 px)'
    },
    intro_clip: {
      title: 'Intro Slide',
      desc: 'Set the default look and feel for the intro slide used across all your videos.',
      template_type: 'Template Type',
      background: 'Background',
      background_color: 'Color',
      background_image: 'Image',
      bg_template: 'Background Image Template',
      custom_image: 'Custom Image',
      upload_button: 'Upload',
      fill_color: 'Fill Color',
      accent_color: 'Accent Color',
      font_color: 'Font Color',
      font_size: 'Font Size',
      preview: 'Preview',
      preview_desc: 'See how your intro slide will look based on the settings you’ve chosen.',
    },
    voice_setting: {
      title: 'Voice-overs',
      desc: 'Users can set a default voice type to be applied across all videos.',
      narration_voice: 'Narration Voice',
      place_holder: 'Search...',
      voice_style: 'Voice Style',
      tool_tip: 'Your selected voice does not support this feature.'
    },
    video_background: {
      title: 'Video Background',
      desc: 'Select a background template or color for your videos. ',
      template_type: 'Template Type',
      custom_image: 'Custom Image',
      upload_button: 'Upload',
      color: 'Color'
    },
    video_layout: {
      title: 'Video Layouts and Styles',
      desc: 'Customize the layout, borders, and visual effects to define the overall style of your videos.',
      layout: 'Layout',
      layout_desc: 'Choose the structure of your video layout.',
      border_type: 'Border Type',
      border_type_desc: `Choose the shape of your video's border.`,
      border_style: 'Border Style',
      border_style_desc: 'Choose a visual style for the border design.',
      shadow: 'Shadow',
      shadow_desc: 'Customize shadow effects to add depth to your video.',
      bg_score: 'Background Score',
      bg_score_desc: 'Enable the toggle to select a background music for your videos.',
      bg_score_volume: 'Background Score Volume',
      bg_score_volume_desc: 'Adjust the volume using the slider.'
    },
    video_annotation: {
      title: 'Video Annotations',
      desc: 'Select default annotation style for your videos.',
      stroke_color: 'Stroke Color',
      stroke_color_desc: 'Select a default stroke color to use in all your videos.'
    }
  },
  guide_theme: {
    title: 'Guide Theme',
    background_title: 'Background Color',
    background_description:
      'We will customize the guide background to your selected color.',
    text_title: 'Font Color',
    text_description:
      'We will customize the guide font to your selected color.',
    button_title: 'Button Color',
    button_description:
      'We will customize the guide button to your selected color.',
    spotlight_title: 'Spotlight Color',
    spotlight_description:
      'We will customize the guide spotlight to your selected color.'
  },
  transfer_owner: {
    action: 'Transfer your workspace ownership',
    title: 'Transfer Workspace Ownership',
    description:
      'This action is permanent and can only be reversed by the new owner.',
    save_button: 'Confirm Transfer',
    success: 'Owner Transfered Successfully'
  },
  billing: {
    manage_payments: {
      label: 'Payment Details',
      description: 'View and modify your payment method used for payments.',
      cta: 'Update'
    },
    invoices: {
      label: 'Invoices',
      description: 'View and download your payment history.',
      cta: 'View Invoices'
    },
    cancel_subscription: {
      label: 'Cancel Subscription',
      description:
        'Cancel your subscription anytime. After cancellation, no further charges will apply.  You can access your workspace until your trial ends or the current billing month concludes.',
      reactivate_description: 'Subscription cancellation: %{date}',
      cta: 'Cancel Subscription',
      reactivate_cta: 'Reactivate Cancelled Subscription'
    },
    billing_details: {
      label: 'Billing Details',
      description: 'View and modify your billing details.',
      cta: 'Update'
    }
  },
  addons: {
    license: {
      title: 'User Licenses',
      desc:
        'Your plan includes %{allowedLicenses} licenses, and you have purchased %{additionalLicenses} additional license(s). You can buy more for $50/month per license.',
      button: 'Manage Licenses',
      modal_title: 'Manage Additional Licenses',
      adjust: 'Adjust the number of licenses you need:',
      none:
        'Your plan includes %{allowedLicenses} licenses. You can buy more for $50/month per license.',
      yearly:{
        desc: 'Your plan includes %{allowedLicenses} licenses, and you have purchased %{additionalLicenses} additional license(s). You can buy more for $600/user/year',
        none:
        'Your plan includes %{allowedLicenses} licenses. You can buy more for $600/user/year.',
      }
    },
    content_bundle: {
      title: 'Content Bundle',
      desc:
        'Your plan includes %{allowedContentBundles} videos and guides, and you have purchased %{additionalContentBundles} additional videos and guides. You can buy an additional bundle of 250 videos and guides at $250/month.',
      button: 'Add Content Bundle',
      modal_title: 'Add Content Bundle',
      none:
        'Your plan includes %{allowedContentBundles} videos and guides. You can buy an additional bundle of 250 videos and guides at $250/month.',
      yearly:{
        desc: 'Your plan includes %{allowedContentBundles} videos and guides, and you have purchased %{additionalContentBundles} additional videos and guides. You can buy an additional bundle of 100 videos and guides at $1,800/year.',
        none: 'Your plan includes %{allowedContentBundles} videos and guides. You can buy an additional bundle of 100 videos and guides at $1,800/year.',
      }
    },
    yearly_maus: 'Your plan includes %{allowedMAU} Monthly Active Users (MAUs). To purchase additional MAUs, contact sales.',
    tiers: {
      title: 'Change Business Plan Tier for More MAUs',
      desc:
        'Your plan includes %{currentMAUCount} Monthly Active Users (MAUs) for your LMS. If you exceed %{currentMAUCount} MAUs, you will be charged an overage of $%{currentOverages}/MAU/mo. Upgrade to a higher tier for additional capacity.',
      button: 'Select Tier',
      tip:
        'Tip: Upgrading tiers is more cost-effective than paying overage fees.',
      modal_title: 'Select Your Business Plan Tier'
    },
    cancel: 'Cancel',
    next: 'Next',
    buy: 'Buy',
    no_changes: 'No changes made on your existing subscription.',
    chargeable: 'Chargeable immediately',
    contact_support: 'Contact support',
    for_licenses: 'for additional licenses',
    for_contents: 'for additional content bundle',
    success: 'Your account has been successfully updated.',
    proceed: 'Proceed',
    contact_sales: 'Contact Sales'
  },
  notifications: {
    email_digest: {
      title: 'Video Engagement Emails',
      description:
        'Choose the frequencies, on when you wish to recieve video engagement digest emails.',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly'
    },
    academy_email_digest: {
      title: 'Email Digest - Academy',
      description:
        'Choose the frequencies, on when you wish to recieve academy engagement digest emails.',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly'
    },
    no_notifications: 'No notifications found'
  },
  security: {
    enable: 'Enable SAML',
    sso: 'Single Sign-On',
    login_url: 'Login Url',
    saml_certificate: 'SAML Certificate',
    logout_url: 'Logout URL (Optional)',
    text: {
      saml_saved: 'SAML Config Saved',
      saml_disabled: 'SAML Disabled',
      invalid_login_url: 'Please enter valid login url',
      invalid_saml_certificate: 'Please enter valid SAML Certificate',
      invalid_logout_url: 'Please enter valid logout url',
      save_error: 'Please correct the errors before saving.',
      enable_desc:
        'Once enabled users will be able to login only with Single Sign-On.',
      login_url_desc: 'Users will be redirected to this URL to login.',
      saml_certificate_desc:
        'SAML X.509 certificate to authenticate with your Identity Provider.',
      logout_url_desc:
        'Users will be redirected to this URL when they logout from app.'
    }
  },
  current_plan: 'You are currently on %{plan_name} plan.',
  next_billing_on: 'Your next billing is on %{next_billing_date}.',
  members: {
    label: 'Email',
    placeholder: 'John@yoda.io',
    user: 'User',
    role: 'Role',
    send_digest_emails: 'Send Digest Emails',
    status: 'Status',
    actions: 'Actions'
  },
  invites: {
    title: 'Pending Invites',
    re_invite: 'Re-invite',
    revoke: 'Revoke'
  },
  pricing: {
    old_pricing: `You are currently in our old pricing structure. Kindly <a href="mailto:support@trainn.co"> contact us </a> to unlock the advantages of our new pricing.`,
    view_features: 'View Features',
    next_billing: 'Your Next Billing is on',
    free: {
      title: 'Free',
      caption:
        ' For new teams with big ideas, begin your video creation journey here.',
      1: 'Unlimited Walkthrough Videos',
      2: 'Unlimited Instant Videos',
      3: '5 collaborators',
      4: 'Video Sharing',
      5: 'Video Embed',
      6: 'Video level analytics',
      7: 'up to 100 MAUs (Monthly unique learners who watch the videos)',
      8: 'Add voice-overs in only one language',
      9: 'In-app Widget(coming soon)'
    },
    standard: {
      title: 'Standard Plan',
      caption:
        'For teams who need better workflows and track customer level analytics.',
      1: 'Unlimited Videos',
      2: 'Unlimited Learning Paths',
      3: 'Unlimited Collaborator',
      4: 'Knowledge Hub',
      5: 'Detailed Analytics',
      6: '%{allowed_minutes} engagement minutes every month',
      note: `Additional $1 for %{overage_minutes} engagement minutes`
    },
    growth: {
      title: 'Studio',
      caption:
        'Boost your pipeline with comprehensive viewer analytics and integrations that you love.',
      1: 'Everything in Free Plan',
      2: 'Learners module',
      3: 'Learners analytics',
      4: 'Up to 500 MAUs*',
      5: 'Intercom widget',
      6: 'Youtube Integration',
      7: 'Remove Trainn Branding',
      8: 'Unlimited Collections',
      9: 'Unlimited Collaborator',
      10: 'Add Voice-over in multiple languages',
      11: 'Custom Domain'
    },
    premium: {
      title: 'Premium',
      caption:
        'Create high-impact video academy for complete digital transformation.',
      1: 'Everything in Premium Plan',
      2: 'Up to 2000 MAUs*',
      3: 'Video Academy',
      4: 'SSO Authentication',
      5: 'Academy Personalisation',
      6: 'Export as PDF',
      7: 'Download Videos',
      8: 'Add watermark in all videos'
    },
    enterprise: {
      title: 'Enterprise Plan',
      caption: 'For teams who want to customized settings',
      1: 'Custom Pricing based on MAUs',
      2: 'Custom Roles & Permissions',
      3: 'Build a custom voice and use for all videos',
      4: 'Dedicated account manager',
      5: 'Custom Contract'
    },
    addon: {
      tag: 'Addon',
      brand: {
        title: 'Brand Personalisation',
        features: {
          1: 'Custom Domain',
          2: 'Remove Trainn Branding',
          3: 'Logo Watermarking'
        }
      }
    },
    custom: {
      standard_title: 'Standard Plan',
      standard_caption: 'Everything you need to train your customers',
      enterprise_title: 'Enterprise Plan',
      enterprise_caption:
        'For organizations that need additional control and end to end support',
      tagline: 'Only pay for what you need. Our pricing is based on,',
      pricing: {
        1: 'Number of Monthly Active Users',
        2: 'Number of Collaborators',
        3: 'Number of Videos'
      },
      features: {
        1: 'Academy with Learning Flow',
        2: 'Detailed Analytics',
        3: 'Custom Domain',
        4: 'Brand Personalization',
        5: 'Custom Roles & Permissions',
        6: 'Dedicated Account Manager',
        7: 'Custom contract',
        everything: 'Everything in Standard'
      }
    }
  },
  integrations: {
    title: 'Integrations',
    description:
      'Extend your Trainn experience by connecting with your favorite tools.',
    connect: 'Connect',
    disconnect: 'Disconnect',
    contact: 'Contact Support',
    youtube: {
      contact_support: `Kindly <a href="mailto:support@trainn.co"> contact us </a> to enable youtube integration for your workspace.`,
      title: 'YouTube Integration',
      desc: 'Share your videos directly into YouTube.',
      connected_desc: `Your YouTube channel <b>%{name}</b> has been connected to Trainn.`,
      channel_name: 'Channel Name',
      watch_tutorial: 'Watch how to integrate'
    },
    webhooks: {
      title: 'Webhook',
      outer_desc: 'Trainn sends video, course, and engagement activity directly into your own systems.',
      learn_more: 'Learn more'
    },
    intercom: {
      contact_support: `Kindly <a href="mailto:support@trainn.co"> contact us </a> to enable intercom integration for your workspace.`,
      title: 'Intercom Integration',
      desc:
        'Intercom makes communication with customers simple, personal and fun. Integrate Trainn with Intercom to send help videos to select customers, and more. Offer a flawless support interaction experience, and make the most of the relationship with your customers.',
      outer_desc:
        'Share Trainn videos directly in your Intercom inbox. Improve customer support with instant access to helpful videos.',
      connected_desc:
        'You Intercom workspace %{name} has been connected to Trainn.',
      channel_name: 'Workspace Name',
      connect_from_appstore:
        'You can easily connect the Trainn application to your Intercom from the Intercom app store.',
      map_customers_title: 'Map customers from Intercom to Trainn',
      map_customers_desc:
        'Choose a unique field to create and sync customers from Intercom to Trainn.',
      map_customers_via_email: 'Use Email',
      map_customers_via_userid:
        "If unchecked Intercom's User Id will be used as unique identifier",
      content_title: 'Manage videos that has to be displayed in Intercom',
      content_desc:
        'Videos added here will be available in your Intercom widget',
      watch_tutorial: 'Watch how to integrate'
    },
    salesforce: {
      title: 'Salesforce Integration',
      outer_desc:
        'Connect and sync learner data from Trainn onto your Salesforce app.',
      connect_section_title: 'Connect with Salesforce',
      connect_section_desc:
        'Sync learner data like courses enrolment, analytics, and more',
      instance_url: 'Salesforce Instance URL',
      installed_by: 'Installed By',
      add_package: 'Add Package',
      add_package_title: 'Add Trainn Package to Salesforce',
      add_package_desc:
        'The package will add Custom Objects and fields to your contacts.',
      add_package_content:
        'The package will add Custom Objects and fields to your contacts.',
      details_title: "What you'll need",
      details_point1: 'A Salesforce account with admin permissions',
      details_point2: 'A Salesforce edition with API access',
      sync_setting_title: 'Sync Settings',
      sync_setting_desc:
        'Map the right fields from Trainn with the contacts in your Salesforce',
      create_contact_if_missing: 'Create Contact in Salesforce if Missing',
      create_contact_if_missing_desc:
        'If the learner is missing in salesforce contact, Create Contact in salesforce fo the learner.',
      already_added: 'Added package already?',
      refresh: 'Refresh to update',
      before_installation: {
        title: 'You have not added the Trainn Package',
        desc:
          'This is essential to sync Trainn data to your contacts in Salesforce'
      },
      package_installed: 'Trainn package installed'
    },
    stripe: {
      title: 'Stripe Connect',
      outer_desc:
        'Connect your stripe account and start creating paid courses.',
      connect_section_title: 'Connect with Stripe',
      connect_section_desc:
        'Connect your stripe account and start creating paid courses.',
      connected_desc: `Your stripe account has been connected to Trainn.`,
      connect_details: 'Stripe Connect Details',
      charges: 'Charges',
      payouts: 'Payouts',
      currency: 'Currency',
      country: 'Country',
      business_name: 'Business Name',
      email: 'Email'
    },
    elevenlabs: {
      title: 'ElevenLabs Integration',
      outer_desc:
        'Connect your ElevenLabs account and use the voices in Trainn',
      connected_desc: 'Your ElevenLabs account has been connected to Trainn',
      selected_voices: 'Selected Voices',
      select_voice: 'Add Voice',
      connect: 'Connect',
      voice_id: 'Add the elevenlabs id of the voice which you wish to add.',
      add: 'Add'
    },
    accredible: {
      title: 'Accredible Integration',
      desc: 'Create and issue branded digital certificates that showcase achievement, drive engagement.',
      outer_desc: 'Create and issue branded digital certificates and badges'
    },
    mcp: {
      title: 'Trainn MCP',
      desc:
        'Let AI assistants like Claude, ChatGPT and Cursor build and manage courses in this workspace.',
      outer_desc:
        'Connect AI assistants to Trainn through MCP.',
      enable: 'Enable',
      disable: 'Disable',
      status_enabled: 'Enabled',
      status_disabled: 'Disabled',
      enabled_title: 'MCP is enabled for this workspace',
      enabled_desc:
        'Members can now connect an AI assistant to this workspace. Each person still signs in and approves permissions, and their Trainn role continues to apply to every action.',
      disabled_title: 'MCP is disabled for this workspace',
      disabled_desc:
        'Nobody can connect an AI assistant to this workspace yet. Enable it to let members authorize their own connections.',
      disable_title: 'Disable Trainn MCP?',
      disable_warning:
        'Every AI assistant currently connected to this workspace will be disconnected immediately. Members will need to connect again if you turn MCP back on.',
      server_url: 'MCP server URL',
      server_url_desc:
        'Share this URL with members who are adding Trainn to their AI assistant.',
      copy: 'Copy',
      copied: 'MCP server URL copied',
      admin_only:
        'Only workspace admins can enable or disable Trainn MCP.'
    }
  },
  disabled_reason: `%{feature} does not match the allowed configuration`,
  access_limit: {
    collaborators: 'You have only %{allowedLicenses} user license(s) in your current plan. Upgrade to add more users. ',
    business_plan_collaborators:'You have only %{allowedLicenses} user license(s) in your current plan. Buy additional user licenses.',
    collection:
      'Oops! Seems you’ll need a higher plan to start creating your collection!',
    download_images:
      'Looking to download images? You’ll need to upgrade your plan.',
    download_pdf: 'Looking to download PDF? You’ll need to upgrade your plan.',
    upload_videos:
      'Want to upload your own videos? No problem. Upgrade now and get started.',
    insta_videos:
      'Oops! Seems you’ll need a higher plan to start creating instant videos!',
    watermark:
      'Looking to add watermarks to your video? You’ll need to upgrade your plan.',
    hub: 'Oops! Seems you’ll need a higher plan to enable Knowledge Hub!',
    flow: 'Unlock Flow!',
    learner:
      "You can add learners only from Plus plan or above. Upgrade your plan to proceed.",
    learner_group:
      "You can add learner groups only from Plus plan or above. Upgrade your plan to proceed.",
    youtube: 'Need to integrate YouTube? No problem! Try Trainn Studio.',
    intercom: 'Need to integrate Intercom? No problem! Try Trainn Studio.',
    academy: 'Uh-oh! Seems you’ll need a higher plan to enable video academy!',
    salesforce: 'Need to integrate Salesforce? Contact Support.',
    stripe: 'Need to connect with Stripe?'
  },
  email: {
    title: 'Emails',
    desc: 'Manage email settings and notification templates.',
    banner: 'Your Academy emails are not getting delivered. Please check your email settings.',
    options: {
      email_settings: 'Email Settings',
      email_notifications: 'Academy Email Notifications'
    },
    server: {
      smtp_option: 'SMTP Option',
      smtp_option_desc: 'Choose SMTP Option',
      smtp_option_placeholder: 'Choose SMTP Option',
      from_email: 'From Email',
      from_email_desc: 'From Email',
      from_email_placeholder: 'Enter From Email',
      from_name: 'From Name',
      from_name_desc: 'Display Name',
      from_name_placeholder: 'Enter From Name',
      reply_to_name_placeholder: 'Enter Reply to Name',
      reply_to_email_placeholder: 'Enter Reply to Email',
      reply_to_name: 'Reply-To Name',
      reply_to_name_dec:
        'This is the name that will appear next to the "Reply-To" email address in the email header.',
      reply_to_email: 'Reply-To Email',
      reply_to_email_desc: 'Email to which responses should be sent to',
      address: 'SMTP Server',
      address_desc: 'SMTP Server',
      address_placeholder: 'Enter Server Address',
      username: 'Username',
      username_desc: 'Credential Username',
      username_placeholder: 'Enter Username',
      password: 'Password',
      password_desc: 'Credential Password',
      password_placeholder: 'Enter Password',
      port: 'Port Number',
      port_desc: 'Port Number of server',
      port_placeholder: 'Enter Port Number',
      tls: 'TLS',
      tls_desc: 'Enable TLS',
      api_key_placeholder: 'Enter API Key',
      api_key: 'API Key'
    }
  }
};
